import React, { useEffect, useState } from 'react';
import { X, Check, Info } from 'lucide-react';
import { Button } from './ui/Button';
import { supabaseClient } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
interface MenuItemOptionsModalProps {
  menuItem: any;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedOptions: any[], totalPrice: number) => void;
  taxExempt?: boolean;
}
export function MenuItemOptionsModal({
  menuItem,
  isOpen,
  onClose,
  onConfirm,
  taxExempt = false
}: MenuItemOptionsModalProps) {
  const [sections, setSections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  useEffect(() => {
    if (isOpen && menuItem?.id) {
      loadSections();
      setTotalPrice(menuItem.price);
      setSelections({});
      setShowValidationErrors(false);
    }
  }, [isOpen, menuItem, taxExempt]);
  const loadSections = async () => {
    setLoading(true);
    try {
      let data = await supabaseClient.getMenuItemSections(menuItem.id);
      const sortedData = (data || []).sort((a: any, b: any) => a.displayOrder - b.displayOrder);
      for (const section of sortedData) {
        if (section.id && (!section.options || section.options.length === 0)) {
          try {
            const {
              getSupabase
            } = await import('../lib/supabase');
            const client = getSupabase();
            if (client) {
              const {
                data: optionsData,
                error
              } = await client.from('menu_section_options').select('*').eq('section_id', section.id).order('display_order', {
                ascending: true
              });
              if (!error && optionsData && optionsData.length > 0) {
                section.options = optionsData.map((opt: any) => ({
                  id: opt.id,
                  sectionId: opt.section_id,
                  name: opt.name,
                  priceModifier: parseFloat(opt.price_modifier || 0),
                  available: opt.available,
                  displayOrder: opt.display_order
                }));
              }
            }
          } catch (fallbackError) {
            console.error('Error in fallback option loading:', fallbackError);
          }
        }
      }
      sortedData.forEach((section: any) => {
        if (section.options) {
          section.options.sort((a: any, b: any) => a.displayOrder - b.displayOrder);
        }
      });
      setSections(sortedData);
    } catch (error) {
      console.error('Error loading sections:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleSelection = (sectionId: string, optionId: string, section: any) => {
    setSelections(prev => {
      const currentSelections = prev[sectionId] || [];
      let newSelections: string[];
      if (section.maxSelections === 1) {
        if (currentSelections.includes(optionId) && section.minSelections === 0) {
          newSelections = [];
        } else {
          newSelections = [optionId];
        }
      } else {
        if (currentSelections.includes(optionId)) {
          newSelections = currentSelections.filter(id => id !== optionId);
        } else {
          if (currentSelections.length >= section.maxSelections) return prev;
          newSelections = [...currentSelections, optionId];
        }
      }
      return {
        ...prev,
        [sectionId]: newSelections
      };
    });
  };
  useEffect(() => {
    if (!menuItem) return;
    let modifiersTotal = 0;
    Object.entries(selections).forEach(([sectionId, optionIds]) => {
      const section = sections.find(s => s.id === sectionId);
      if (section) {
        optionIds.forEach(optId => {
          const option = section.options.find((o: any) => o.id === optId);
          if (option) modifiersTotal += option.priceModifier;
        });
      }
    });
    setTotalPrice(menuItem.price + modifiersTotal);
  }, [selections, sections, menuItem, taxExempt]);
  const getSectionValidationState = (section: any) => {
    const selectedCount = (selections[section.id] || []).length;
    const minRequired = section.required ? Math.max(section.minSelections, 1) : section.minSelections;
    if (section.required && selectedCount < minRequired) {
      return {
        isValid: false,
        message: `Selecciona al menos ${minRequired}`
      };
    }
    if (selectedCount > section.maxSelections) {
      return {
        isValid: false,
        message: `Máximo ${section.maxSelections} opciones`
      };
    }
    return {
      isValid: true
    };
  };
  const validateAll = () => {
    for (const section of sections) {
      const {
        isValid
      } = getSectionValidationState(section);
      if (!isValid) return false;
    }
    return true;
  };
  const handleConfirm = () => {
    if (!validateAll()) {
      setShowValidationErrors(true);
      return;
    }
    const selectedOptions: any[] = [];
    Object.entries(selections).forEach(([sectionId, optionIds]) => {
      const section = sections.find(s => s.id === sectionId);
      if (section) {
        optionIds.forEach(optId => {
          const option = section.options.find((o: any) => o.id === optId);
          if (option) {
            selectedOptions.push({
              sectionId: section.id,
              sectionName: section.name,
              optionId: option.id,
              optionName: option.name,
              priceModifier: option.priceModifier
            });
          }
        });
      }
    });
    onConfirm(selectedOptions, totalPrice);
  };
  // Shared options renderer
  const renderSections = () => {
    if (loading) {
      return <div className="space-y-4" data-id="element-825">
          {[1, 2, 3].map(i => <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl h-32 animate-pulse" data-id="element-826" />)}
        </div>;
    }
    if (sections.length === 0) {
      return <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-827">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-828">
            <Check size={28} className="text-gray-400" data-id="element-829" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-830">
            Todo listo
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-831">
            Este plato no requiere configuración adicional.
          </p>
        </div>;
    }
    return sections.map(section => {
      const validation = getSectionValidationState(section);
      const hasError = showValidationErrors && !validation.isValid;
      const currentSelectionCount = (selections[section.id] || []).length;
      const isMaxReached = currentSelectionCount >= section.maxSelections;
      const availableOptions = (section.options || []).filter((opt: any) => opt.available);
      return <div key={section.id} className={`bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border transition-all ${hasError ? 'border-red-400 ring-1 ring-red-400' : 'border-gray-100 dark:border-gray-800'}`} data-id="element-832">
          {/* Section Header */}
          <div className="px-4 py-3 border-b border-gray-50 dark:border-gray-800 flex justify-between items-center" data-id="element-833">
            <div className="flex items-center gap-2" data-id="element-834">
              <h3 className="font-bold text-base text-gray-900 dark:text-white" data-id="element-835">
                {section.name}
              </h3>
              {section.required ? <span className="bg-emerald-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide" data-id="element-836">
                  Obligatorio
                </span> : <span className="bg-gray-100 dark:bg-gray-800 text-gray-500 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide" data-id="element-837">
                  Opcional
                </span>}
            </div>
            <div className={`text-xs font-bold tabular-nums ${hasError ? 'text-red-500' : currentSelectionCount > 0 ? 'text-emerald-600' : 'text-gray-400'}`} data-id="element-838">
              {currentSelectionCount}/{section.maxSelections}
            </div>
          </div>

          {/* Options */}
          <div className="p-2 space-y-0.5" data-id="element-839">
            {availableOptions.length === 0 ? <div className="p-4 text-center text-sm text-gray-400" data-id="element-840">
                <Info size={18} className="mx-auto mb-1 opacity-50" data-id="element-841" />
                Sin opciones disponibles
              </div> : availableOptions.map((option: any) => {
            const isSelected = (selections[section.id] || []).includes(option.id);
            const isDisabled = !isSelected && isMaxReached && section.maxSelections > 1;
            return <div key={option.id} onClick={() => {
              if (!isDisabled) handleSelection(section.id, option.id, section);
            }} className={`relative flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-150 ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'} ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/10' : ''}`} data-id="element-842">
                    {isSelected && <div className="absolute left-0 top-2 bottom-2 w-0.5 bg-emerald-500 rounded-r-full" data-id="element-843" />}
                    <div className="flex items-center gap-3 pl-1" data-id="element-844">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-845">
                        {isSelected && <Check size={11} className="text-white" strokeWidth={3} data-id="element-846" />}
                      </div>
                      <span className={`text-sm font-medium ${isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-847">
                        {option.name}
                      </span>
                    </div>
                    {option.priceModifier > 0 && <span className={`text-xs font-bold tabular-nums shrink-0 ${isSelected ? 'text-emerald-600' : 'text-gray-400'}`} data-id="element-848">
                        +RD$ {option.priceModifier.toFixed(2)}
                      </span>}
                  </div>;
          })}
          </div>
        </div>;
    });
  };
  if (!menuItem) return null;
  const displayPrice = (taxExempt ? totalPrice / 1.18 : totalPrice).toFixed(2);
  const isInvalid = showValidationErrors && !validateAll();
  return <AnimatePresence data-id="element-849">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 z-[60]" data-id="element-850" />

          {/* ── MOBILE: bottom sheet ── */}
          <div className="md:hidden fixed inset-x-0 bottom-0 top-0 z-[60] flex items-end justify-center pointer-events-none pt-16" data-id="element-851">
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 300
        }} className="bg-white dark:bg-gray-900 w-full rounded-t-3xl shadow-2xl overflow-hidden flex flex-col max-h-full pointer-events-auto" data-id="element-852">
              {/* Mobile header */}
              <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 dark:border-gray-800 shrink-0" data-id="element-853">
                <h2 className="text-base font-bold text-gray-900 dark:text-white truncate pr-3" data-id="element-854">
                  {menuItem.name}
                </h2>
                <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500" data-id="element-855">
                  <X size={16} data-id="element-856" />
                </button>
              </div>
              {/* Mobile thumbnail row */}
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800 shrink-0" data-id="element-857">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-200 dark:bg-gray-700 shrink-0" data-id="element-858">
                  {menuItem.image ? <img src={menuItem.image} alt={menuItem.name} className="w-full h-full object-cover" data-id="element-859" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-860">
                      <span className="text-2xl" data-id="element-861">🍽️</span>
                    </div>}
                </div>
                <div className="flex-1 min-w-0" data-id="element-862">
                  {menuItem.description && <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-1.5" data-id="element-863">
                      {menuItem.description}
                    </p>}
                  <div className="inline-flex bg-gray-900 dark:bg-white text-white dark:text-black text-sm font-bold px-3 py-1 rounded-lg" data-id="element-864">
                    RD${' '}
                    {(taxExempt ? menuItem.price / 1.18 : menuItem.price).toFixed(2)}
                  </div>
                </div>
              </div>
              {/* Mobile scrollable options */}
              <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-black/20 p-3 space-y-3" data-id="element-865">
                {renderSections()}
              </div>
              {/* Mobile footer */}
              <div className="bg-white dark:bg-gray-900 px-4 py-4 border-t border-gray-100 dark:border-gray-800" data-id="element-866">
                <div className="flex justify-between items-center mb-3" data-id="element-867">
                  <div data-id="element-868">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400" data-id="element-869">
                      Total a pagar
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums" data-id="element-870">
                      RD$ {displayPrice}
                    </p>
                  </div>
                  {taxExempt && <span className="text-[10px] font-medium text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full" data-id="element-871">
                      Exento ITBIS
                    </span>}
                </div>
                <Button onClick={handleConfirm} fullWidth disabled={loading} className={`h-13 rounded-2xl text-base font-bold shadow-lg ${isInvalid ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-gray-900 hover:bg-black text-white'}`} data-id="element-872">
                  {isInvalid ? 'Completa las opciones requeridas' : 'Agregar al Pedido'}
                </Button>
              </div>
            </motion.div>
          </div>

          {/* ── DESKTOP: centered wide modal with image left + options right ── */}
          <div className="hidden md:flex fixed inset-0 z-[60] items-center justify-center p-6 pointer-events-none" data-id="element-873">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 10
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 350
        }} className="bg-white dark:bg-gray-900 w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-row pointer-events-auto" style={{
          maxHeight: 'calc(100vh - 80px)'
        }} data-id="element-874">
              {/* Left: Image column — sticky, full height */}
              <div className="w-80 shrink-0 relative bg-gray-900 flex flex-col overflow-hidden" data-id="element-875">
                <div className="absolute inset-0" data-id="element-876">
                  {menuItem.image ? <>
                      {/* Blurred background to fill space */}
                      <img src={menuItem.image} alt="" className="w-full h-full object-cover scale-110 blur-xl opacity-60" aria-hidden="true" data-id="element-877" />
                      {/* Main image — contained, centered */}
                      <img src={menuItem.image} alt={menuItem.name} className="absolute inset-0 w-full h-full object-contain" data-id="element-878" />
                    </> : <div className="w-full h-full flex items-center justify-center bg-gray-800" data-id="element-879">
                      <span className="text-7xl" data-id="element-880">🍽️</span>
                    </div>}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" data-id="element-881" />
                </div>

                {/* Close button */}
                <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-black/50 border border-white/20 text-white hover:bg-black/70 transition-colors z-10" data-id="element-882">
                  <X size={16} data-id="element-883" />
                </button>

                {/* Product info overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10" data-id="element-884">
                  <h2 className="text-2xl font-bold text-white leading-tight mb-2" data-id="element-885">
                    {menuItem.name}
                  </h2>
                  {menuItem.description && <p className="text-white/70 text-sm leading-relaxed mb-4 line-clamp-3" data-id="element-886">
                      {menuItem.description}
                    </p>}
                  <div className="flex items-center gap-2" data-id="element-887">
                    <div className="bg-white rounded-full px-4 py-1.5 text-gray-900 text-sm font-bold shadow-lg" data-id="element-888">
                      RD${' '}
                      {(taxExempt ? menuItem.price / 1.18 : menuItem.price).toFixed(2)}
                    </div>
                    {taxExempt && <span className="text-white/60 text-xs font-medium" data-id="element-889">
                        Exento ITBIS
                      </span>}
                  </div>
                </div>
              </div>

              {/* Right: Options + footer */}
              <div className="flex flex-col flex-1 min-w-0 min-h-0" data-id="element-890">
                {/* Desktop header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0 flex items-center justify-between" data-id="element-891">
                  <div data-id="element-892">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-893">
                      Personaliza tu pedido
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-894">
                      {sections.length > 0 ? `${sections.length} sección${sections.length > 1 ? 'es' : ''} para configurar` : 'Sin opciones adicionales'}
                    </p>
                  </div>
                  <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-895">
                    <X size={18} data-id="element-896" />
                  </button>
                </div>

                {/* Scrollable options */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50/50 dark:bg-black/10" data-id="element-897">
                  {renderSections()}
                </div>

                {/* Desktop footer */}
                <div className="bg-white dark:bg-gray-900 px-6 py-5 border-t border-gray-100 dark:border-gray-800 shrink-0" data-id="element-898">
                  <div className="flex items-center justify-between mb-4" data-id="element-899">
                    <div data-id="element-900">
                      <p className="text-xs uppercase tracking-wider font-bold text-gray-400 mb-0.5" data-id="element-901">
                        Total a pagar
                      </p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums" data-id="element-902">
                        RD$ {displayPrice}
                      </p>
                    </div>
                    <Button onClick={handleConfirm} disabled={loading} className={`h-12 px-8 rounded-xl text-base font-bold shadow-lg ${isInvalid ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-gray-900 hover:bg-black text-white'}`} data-id="element-903">
                      {isInvalid ? 'Completa las opciones' : 'Agregar al Pedido →'}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}